/**
 * Wires the `runner.add_mcp_server` and `runner.attach_instance` capabilities
 * to the live session's `ExternalMcpManager` + `PreMintedSecretProvider`. Free
 * of `serve.ts` imports — the dependency direction stays acyclic. The caller
 * passes getters because `serve.ts` lazily constructs the manager and secrets
 * provider.
 *
 * @category Runtime
 * @since 3.6.0
 */
import { join } from "node:path";
import {
  deriveSingleMaterializedMcpDeclaration,
  type McpServerDeclaration,
} from "@skaile/workspaces/core";
import type { PreMintedSecretProvider } from "@skaile/workspaces/connectors";
import { McpServerAlreadyRegisteredError } from "./external-mcp.js";
import type { ExternalMcpManager } from "./external-mcp.js";
import { toPreMinted } from "./session-resource-handlers.js";

export interface McpRuntimeHandlerDeps {
  /** Returns `null` during the brief session-recreate gap; guard before use. */
  getOrCreateMcpManager: () => ExternalMcpManager | null;
  /** `null` when the session has no pre-minted secret store yet (CLI mode). */
  getPreMintedSecrets: () => PreMintedSecretProvider | null;
  /** True when the active driver picks up newly-registered capabilities without a session restart. */
  driverPicksUpToolsLive: () => boolean;
  /**
   * Record a live-attached server in serve's registry so every later session
   * rebuild respawns it. Without it the rebuild reads `skaile.yaml` only, which
   * a live attach never updates, and the server is dropped for good.
   */
  registerLiveAttachedMcp: (declaration: McpServerDeclaration) => void;
  /**
   * Queue a driver restart so a non-live driver re-bakes its tool set (owned by
   * `serve.ts`). Fire-and-forget by contract: the platform caps this capability
   * call at 30s and a rebuild routinely exceeds that.
   */
  scheduleDriverRestartForToolChanges: (reason: string) => void;
  emitResourcesAvailable: () => void;
  log: (msg: string) => void;
  /**
   * Absolute path to the session project directory (used by `attachInstance`
   * for materialized asset lookup). Required when `attachInstance` is invoked.
   */
  getProjectDir?: () => string;
}

export interface McpRuntimeHandlers {
  addMcpServer?: (input: {
    declaration: McpServerDeclaration;
    preMintedToken?: { token: string; expiresAt?: string | null };
  }) => Promise<
    | { ok: true; id: string; live: boolean; toolCount: number }
    | { ok: false; code: string; message: string }
  >;

  /**
   * Materialize-then-attach-by-ref: the platform has already written
   * `<projectDir>/.skaile/assets/mcp-server/<name>/MCP.md` (+ optional
   * `.instance.json` with `resolvedSecrets`). The runner self-derives the
   * declaration from disk and attaches it live via `ExternalMcpManager.addServer`.
   * Connectors return `unsupported_kind` (deferred — Phase 2.5).
   */
  attachInstance?: (input: {
    kind: string;
    name: string;
    preMintedToken?: { token: string; expiresAt?: string | null };
  }) => Promise<
    | { ok: true; id: string; live: boolean; toolCount: number }
    | { ok: false; code: string; message: string }
  >;
}

export function buildMcpRuntimeHandlers(deps: McpRuntimeHandlerDeps): McpRuntimeHandlers {
  /**
   * `getOrCreateExternalMcpManager` throws when the session fell back to its
   * no-resources result (no capability registry). Report that as `no_session`
   * rather than letting the throw escape as an unhandled capability rejection.
   */
  const resolveManager = (): ExternalMcpManager | null => {
    try {
      return deps.getOrCreateMcpManager();
    } catch (err) {
      deps.log(
        `[mcp-runtime] MCP manager unavailable: ${err instanceof Error ? err.message : String(err)}`,
      );
      return null;
    }
  };

  /**
   * Make a just-connected server durable and resolve its `live` flag. Recording
   * the declaration is what survives later session rebuilds; claude-sdk bakes
   * its tool set at driver creation, so it additionally needs the restart.
   *
   * Never throws — an escape would reach the caller's `addServer` catch, unstage
   * the bearer and report a failure for an attach that in fact succeeded.
   */
  const settleLive = (decl: McpServerDeclaration): boolean => {
    try {
      deps.registerLiveAttachedMcp(decl);
      if (deps.driverPicksUpToolsLive()) return true;
      // Backgrounded by contract, and honest: the swap gate arms synchronously,
      // so the next turn already waits for the rebuilt driver.
      deps.scheduleDriverRestartForToolChanges(`MCP server '${decl.id}'`);
      return true;
    } catch (err) {
      deps.log(
        `[mcp-runtime] settling ${decl.id} live failed: ${err instanceof Error ? err.message : String(err)}`,
      );
      return false;
    }
  };

  return {
    addMcpServer: async (input) => {
      const decl = input.declaration;
      if (!decl?.id) {
        return { ok: false, code: "invalid_declaration", message: "declaration.id is required" };
      }
      const mgr = resolveManager();
      if (!mgr) return { ok: false, code: "no_session", message: "session resources not ready" };
      // Stage the pre-minted bearer under mcp:<id> so transport construction resolves it.
      // toPreMinted adds ok:true — without it, resolve() silently returns undefined.
      if (input.preMintedToken) {
        deps.getPreMintedSecrets()?.set("mcp", decl.id, toPreMinted(input.preMintedToken));
      }
      try {
        const { toolNames } = await mgr.addServer(decl);
        // Settle first: the emit stamps each server's `live` flag, and only
        // settling has decided what this one's is.
        const live = settleLive(decl);
        deps.emitResourcesAvailable();
        deps.log(
          `[mcp-runtime] runner.add_mcp_server connected ${decl.id} (${toolNames.length} tools)`,
        );
        return { ok: true, id: decl.id, live, toolCount: toolNames.length };
      } catch (err) {
        // Roll back the staged bearer so a later retry can re-stage cleanly.
        deps.getPreMintedSecrets()?.unset("mcp", decl.id);
        const message = err instanceof Error ? err.message : String(err);
        const code =
          err instanceof McpServerAlreadyRegisteredError
            ? "mcp_already_registered"
            : "mcp_connect_failed";
        return { ok: false, code, message };
      }
    },

    attachInstance: async (input) => {
      if (input.kind !== "mcp-server") {
        return {
          ok: false,
          code: "unsupported_kind",
          message: `attach_instance supports mcp-server only; '${input.kind}' is not yet supported`,
        };
      }
      if (!input.name) {
        return { ok: false, code: "invalid_input", message: "name is required" };
      }
      if (input.name.includes("/") || input.name.includes("\\") || input.name.includes("..")) {
        return {
          ok: false,
          code: "invalid_input",
          message: "name must not contain path separators or traversal segments",
        };
      }
      if (!deps.getProjectDir) {
        return { ok: false, code: "no_project_dir", message: "getProjectDir not configured" };
      }
      const subDir = join(deps.getProjectDir(), ".skaile/assets/mcp-server", input.name);
      const decl = deriveSingleMaterializedMcpDeclaration(subDir, input.name);
      if (!decl) {
        return {
          ok: false,
          code: "not_materialized",
          message: `no materialized mcp-server at ${subDir}`,
        };
      }
      const mgr = resolveManager();
      if (!mgr) return { ok: false, code: "no_session", message: "session resources not ready" };
      // Stage the pre-minted bearer under mcp:<id> so transport construction resolves it.
      // toPreMinted adds ok:true — without it, resolve() silently returns undefined.
      if (input.preMintedToken) {
        deps.getPreMintedSecrets()?.set("mcp", decl.id, toPreMinted(input.preMintedToken));
      }
      try {
        const { toolNames } = await mgr.addServer(decl);
        // Settle first: the emit stamps each server's `live` flag, and only
        // settling has decided what this one's is.
        const live = settleLive(decl);
        deps.emitResourcesAvailable();
        deps.log(
          `[mcp-runtime] runner.attach_instance connected ${decl.id} (${toolNames.length} tools)`,
        );
        return { ok: true, id: decl.id, live, toolCount: toolNames.length };
      } catch (err) {
        // Roll back the staged bearer so a later retry can re-stage cleanly.
        if (input.preMintedToken) deps.getPreMintedSecrets()?.unset("mcp", decl.id);
        const message = err instanceof Error ? err.message : String(err);
        const code =
          err instanceof McpServerAlreadyRegisteredError
            ? "mcp_already_registered"
            : "mcp_connect_failed";
        return { ok: false, code, message };
      }
    },
  };
}
